import { useEffect, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { ArrowLeft, TrendingUp, X } from 'lucide-react'
import { api } from '@/lib/api'
import {
  STAGE_LABELS,
  STAGE_ORDER,
  serviceLineLabel,
  type PipelineStage,
  type ServiceLine,
} from '@/lib/stages'

interface ClientStubLite {
  id: string
  displayName: string
  email: string | null
  phoneE164: string | null
  tags: string[]
}

interface HistoryEntry {
  id: string
  fromStage: PipelineStage | null
  toStage: PipelineStage
  changedAt: string
  changedByUserId: string | null
}

interface OpportunityDetail {
  id: string
  title: string
  stage: PipelineStage
  serviceLine: string
  monetaryValueCents: number
  costCents: number
  probability: number
  expectedCloseAt: string | null
  actualCloseAt: string | null
  description: string | null
  tags: string[]
  source: string | null
  assignedToUserId: string | null
  lastActivityAt: string | null
  createdAt: string
  updatedAt: string
  lostReasonId: string | null
  clientStub: ClientStubLite
  lostReason: { id: string; name: string } | null
  stageHistory: HistoryEntry[]
}

interface LostReason {
  id: string
  name: string
  active: boolean
}

function eur(cents: number): string {
  return new Intl.NumberFormat('es-ES', {
    style: 'currency',
    currency: 'EUR',
    maximumFractionDigits: 0,
  }).format(cents / 100)
}

export function OpportunityDetailPage() {
  const { id } = useParams<{ id: string }>()
  const [opp, setOpp] = useState<OpportunityDetail | null>(null)
  const [err, setErr] = useState<string | null>(null)
  const [showLostModal, setShowLostModal] = useState(false)
  // F2b: GANADA pide confirmación (mismo patrón que PipelinePage).
  const [showWonConfirm, setShowWonConfirm] = useState(false)
  const [lostReasons, setLostReasons] = useState<LostReason[]>([])
  const [serviceLines, setServiceLines] = useState<ServiceLine[]>([])
  const [saving, setSaving] = useState(false)

  // Estado local editable. Sincronizamos sólo cuando carga la opp para evitar
  // pisar cambios en curso del usuario.
  const [title, setTitle] = useState('')
  const [valueEur, setValueEur] = useState('0')
  const [serviceLine, setServiceLine] = useState<string>('CONSULTORIA_IA')
  const [probability, setProbability] = useState(50)
  const [expected, setExpected] = useState('')
  const [description, setDescription] = useState('')

  async function load() {
    if (!id) return
    try {
      const o = await api<OpportunityDetail>(`/v1/marketing/opportunities/${id}`)
      setOpp(o)
      setTitle(o.title)
      setValueEur((o.monetaryValueCents / 100).toString())
      setServiceLine(o.serviceLine)
      setProbability(o.probability)
      setExpected(o.expectedCloseAt ? o.expectedCloseAt.slice(0, 10) : '')
      setDescription(o.description ?? '')
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e))
    }
  }

  useEffect(() => {
    void load()
    void loadReasons()
    void loadServiceLines()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id])

  async function loadReasons() {
    try {
      const r = await api<{ items: LostReason[] }>('/v1/marketing/lost-reasons')
      setLostReasons(r.items.filter((x) => x.active))
    } catch {
      /* noop */
    }
  }

  // Catálogo read-only de líneas de servicio (mirror de JR2_OS). Con
  // includeInactive=true para poder mostrar el label de opps históricas cuya
  // línea fue desactivada en JR2_OS.
  async function loadServiceLines() {
    try {
      const r = await api<{ items: ServiceLine[] }>(
        '/v1/marketing/service-lines?includeInactive=true',
      )
      setServiceLines(r.items)
    } catch {
      /* noop — el select cae al fallback con el code actual */
    }
  }

  async function save() {
    if (!opp) return
    setSaving(true)
    setErr(null)
    try {
      const cents = Math.round(parseFloat(valueEur || '0') * 100)
      await api(`/v1/marketing/opportunities/${opp.id}`, {
        method: 'PATCH',
        body: JSON.stringify({
          title: title.trim(),
          monetaryValueCents: isNaN(cents) ? 0 : cents,
          serviceLine,
          probability,
          expectedCloseAt: expected ? new Date(expected).toISOString() : null,
          description: description || null,
        }),
      })
      await load()
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e))
    } finally {
      setSaving(false)
    }
  }

  async function changeStage(toStage: PipelineStage) {
    if (!opp || opp.stage === toStage) return
    // Terminales con confirmación explícita — coherente con PipelinePage:
    // PERDIDA abre el modal de razón; GANADA (F2b) pide confirmación tanto
    // desde el select de cabecera como desde el botón «Marcar como ganada».
    if (toStage === 'PERDIDA') {
      setShowLostModal(true)
      return
    }
    if (toStage === 'GANADA') {
      setShowWonConfirm(true)
      return
    }
    await doChangeStage(toStage)
  }

  async function doChangeStage(toStage: PipelineStage) {
    if (!opp) return
    try {
      await api(`/v1/marketing/opportunities/${opp.id}/stage`, {
        method: 'POST',
        body: JSON.stringify({ toStage }),
      })
      await load()
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e))
    }
  }

  async function confirmWon() {
    setShowWonConfirm(false)
    await doChangeStage('GANADA')
  }

  async function confirmLost(reasonId: string) {
    if (!opp) return
    try {
      await api(`/v1/marketing/opportunities/${opp.id}/stage`, {
        method: 'POST',
        body: JSON.stringify({ toStage: 'PERDIDA', lostReasonId: reasonId }),
      })
      setShowLostModal(false)
      await load()
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e))
    }
  }

  if (!opp) return <div className="p-6 text-sm font-mono text-ink-700/50">Cargando…</div>

  return (
    <div className="flex h-full">
      <div className="flex-1 overflow-auto">
        <header className="px-6 py-4 border-b border-ink-900/10 bg-white">
          <Link
            to="/marketing/pipeline"
            className="text-blueprint-700 text-xs flex items-center gap-1 mb-2"
          >
            <ArrowLeft className="size-3" /> Pipeline
          </Link>
          <div className="flex items-start justify-between gap-4">
            <div className="flex-1">
              <input
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                className="text-xl font-medium w-full border-b border-transparent hover:border-ink-900/15 focus:border-blueprint-600 focus:outline-none bg-transparent"
              />
              <div className="flex items-center gap-3 text-xs font-mono text-ink-700/70 mt-1">
                <span>creada {new Date(opp.createdAt).toLocaleDateString('es-ES')}</span>
                {opp.actualCloseAt && (
                  <>
                    <span>·</span>
                    <span>cerrada {new Date(opp.actualCloseAt).toLocaleDateString('es-ES')}</span>
                  </>
                )}
              </div>
            </div>
            <select
              value={opp.stage}
              onChange={(e) => void changeStage(e.target.value as PipelineStage)}
              className="border border-ink-900/15 rounded px-2 py-1.5 text-sm bg-white"
            >
              {STAGE_ORDER.map((s) => (
                <option key={s} value={s}>
                  {STAGE_LABELS[s]}
                </option>
              ))}
            </select>
          </div>
        </header>

        <div className="px-6 py-4 space-y-5">
          {err && (
            <div className="text-xs font-mono text-red-700 bg-red-50 border border-red-200 rounded px-2 py-1">
              {err}
            </div>
          )}

          <section className="bp-card p-4 grid grid-cols-2 gap-4">
            <div>
              <label className="text-[10px] font-mono uppercase text-ink-700/60">
                Valor (EUR)
              </label>
              <input
                type="number"
                step="0.01"
                value={valueEur}
                onChange={(e) => setValueEur(e.target.value)}
                className="w-full border border-ink-900/15 rounded px-2 py-1.5 text-sm"
              />
            </div>
            <div>
              <label className="text-[10px] font-mono uppercase text-ink-700/60">
                Línea de servicio
              </label>
              <select
                value={serviceLine}
                onChange={(e) => setServiceLine(e.target.value)}
                className="w-full border border-ink-900/15 rounded px-2 py-1.5 text-sm bg-white"
              >
                {/* Si el code actual no está en el catálogo cargado (aún
                    cargando o línea eliminada), lo mostramos como opción
                    extra para no perder el valor al guardar. Label humanizado
                    (F2a): nunca el code crudo tipo CONSULTORIA_IA. */}
                {!serviceLines.some((sl) => sl.code === serviceLine) && (
                  <option value={serviceLine}>
                    {serviceLineLabel(serviceLine, serviceLines)}
                  </option>
                )}
                {serviceLines.map((sl) => (
                  <option key={sl.code} value={sl.code} disabled={!sl.active}>
                    {sl.name}
                    {!sl.active ? ' (inactiva)' : ''}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-[10px] font-mono uppercase text-ink-700/60">
                Probabilidad ({probability}%)
              </label>
              <input
                type="range"
                min={0}
                max={100}
                value={probability}
                onChange={(e) => setProbability(parseInt(e.target.value, 10))}
                className="w-full"
              />
            </div>
            <div>
              <label className="text-[10px] font-mono uppercase text-ink-700/60">
                Cierre estimado
              </label>
              <input
                type="date"
                value={expected}
                onChange={(e) => setExpected(e.target.value)}
                className="w-full border border-ink-900/15 rounded px-2 py-1.5 text-sm"
              />
            </div>
          </section>

          <section className="bp-card p-4">
            <label className="text-[10px] font-mono uppercase text-ink-700/60">
              Descripción
            </label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={4}
              className="w-full border border-ink-900/15 rounded px-2 py-1.5 text-sm"
              placeholder="Notas internas, alcance, contexto…"
            />
          </section>

          <div className="flex items-center gap-2">
            <button onClick={save} disabled={saving} className="bp-btn-primary">
              {saving ? 'Guardando…' : 'Guardar'}
            </button>
            {opp.stage !== 'GANADA' && opp.stage !== 'PERDIDA' && (
              <>
                <button
                  onClick={() => void changeStage('GANADA')}
                  className="bp-btn bg-emerald-50 text-emerald-700 border-emerald-200"
                >
                  Marcar como ganada
                </button>
                <button
                  onClick={() => setShowLostModal(true)}
                  className="bp-btn bg-red-50 text-red-700 border-red-200"
                >
                  Marcar como perdida
                </button>
              </>
            )}
          </div>

          <section className="bp-card p-4">
            <h3 className="text-xs font-medium uppercase tracking-wider text-ink-700/60 mb-2">
              Historial de stages ({opp.stageHistory.length})
            </h3>
            <ul className="space-y-1">
              {opp.stageHistory.length === 0 && (
                <li className="text-xs text-ink-700/40">Sin historial.</li>
              )}
              {opp.stageHistory.map((h) => (
                <li
                  key={h.id}
                  className="flex items-center gap-2 text-xs font-mono border-b border-ink-900/5 pb-1"
                >
                  <span className="text-ink-700/50">
                    {new Date(h.changedAt).toLocaleString('es-ES')}
                  </span>
                  <span className="text-ink-700/40">·</span>
                  <span>
                    {h.fromStage ? STAGE_LABELS[h.fromStage] : '∅'}
                    {' → '}
                    <span className="text-blueprint-700">{STAGE_LABELS[h.toStage]}</span>
                  </span>
                </li>
              ))}
            </ul>
          </section>

          <section className="bp-card p-4">
            <h3 className="text-xs font-medium uppercase tracking-wider text-ink-700/60 mb-2">
              Notas
            </h3>
            <p className="text-xs text-ink-700/40">Próximamente.</p>
          </section>
        </div>
      </div>

      <aside className="w-72 shrink-0 border-l border-ink-900/10 bg-white overflow-auto">
        <div className="p-4 border-b border-ink-900/10">
          <div className="text-[10px] font-mono uppercase tracking-wider text-ink-700/50 mb-1">
            Cliente
          </div>
          <Link
            to={`/marketing/clients/${opp.clientStub.id}`}
            className="text-sm font-medium text-blueprint-700"
          >
            {opp.clientStub.displayName}
          </Link>
          {opp.clientStub.email && (
            <div className="text-xs font-mono text-ink-700/70 mt-1">
              {opp.clientStub.email}
            </div>
          )}
          {opp.clientStub.phoneE164 && (
            <div className="text-xs font-mono text-ink-700/70">{opp.clientStub.phoneE164}</div>
          )}
          {opp.clientStub.tags.length > 0 && (
            <div className="flex flex-wrap gap-1 mt-2">
              {opp.clientStub.tags.map((t) => (
                <span key={t} className="bp-pill text-[10px]">
                  {t}
                </span>
              ))}
            </div>
          )}
        </div>

        <div className="p-4 border-b border-ink-900/10">
          <div className="text-[10px] font-mono uppercase tracking-wider text-ink-700/50 mb-1 flex items-center gap-1">
            <TrendingUp className="size-3" /> Forecast individual
          </div>
          <div className="text-xs font-mono">
            valor: {eur(opp.monetaryValueCents)}
          </div>
          <div className="text-xs font-mono">
            ponderado:{' '}
            {eur(Math.round((opp.monetaryValueCents * opp.probability) / 100))}
          </div>
        </div>

        {opp.lostReason && (
          <div className="p-4 border-b border-ink-900/10">
            <div className="text-[10px] font-mono uppercase tracking-wider text-red-700 mb-1">
              Razón de pérdida
            </div>
            <div className="text-xs">{opp.lostReason.name}</div>
          </div>
        )}
      </aside>

      {showLostModal && (
        <LostReasonInlineModal
          reasons={lostReasons}
          onCancel={() => setShowLostModal(false)}
          onConfirm={confirmLost}
        />
      )}

      {showWonConfirm && (
        <ConfirmWonModal
          oppTitle={opp.title}
          onCancel={() => setShowWonConfirm(false)}
          onConfirm={() => void confirmWon()}
        />
      )}
    </div>
  )
}

/**
 * Confirmación de GANADA (F2b) — réplica del ConfirmStageModal de
 * PipelinePage (mismo patrón, misma estética y mismo copy) para que cerrar
 * desde el detalle se sienta igual que desde el kanban.
 */
function ConfirmWonModal({
  oppTitle,
  onCancel,
  onConfirm,
}: {
  oppTitle: string
  onCancel: () => void
  onConfirm: () => void
}) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onCancel()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onCancel])

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="confirm-won-title"
      className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4"
      onClick={(e) => {
        if (e.target === e.currentTarget) onCancel()
      }}
    >
      <div className="bp-card bg-white max-w-md w-full p-5">
        <h2 id="confirm-won-title" className="text-base font-medium mb-1">
          Mover a Ganada
        </h2>
        <p className="text-xs text-ink-700/60 mb-3">{oppTitle}</p>
        <p className="text-sm text-ink-900 mb-4">
          Marcar como ganada cierra la oportunidad y dispara las TagRules y webhooks de
          cierre. ¿Confirmar?
        </p>
        <div className="flex justify-end gap-2">
          {/* Foco inicial en Cancelar (política ConfirmDialog): un Enter
              distraído no debe cerrar la oportunidad. */}
          <button onClick={onCancel} className="bp-btn" autoFocus>
            Cancelar
          </button>
          <button onClick={onConfirm} className="bp-btn-primary">
            Confirmar
          </button>
        </div>
      </div>
    </div>
  )
}

function LostReasonInlineModal({
  reasons,
  onCancel,
  onConfirm,
}: {
  reasons: LostReason[]
  onCancel: () => void
  onConfirm: (id: string) => void
}) {
  const [reasonId, setReasonId] = useState<string>(reasons[0]?.id ?? '')

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
      <div className="bp-card bg-white max-w-md w-full p-5">
        <div className="flex items-start justify-between mb-3">
          <h2 className="text-base font-medium">Marcar como perdida</h2>
          <button onClick={onCancel} className="text-ink-700/60 hover:text-ink-900">
            <X className="size-4" />
          </button>
        </div>
        {reasons.length === 0 ? (
          <div className="text-xs text-red-700 bg-red-50 border border-red-200 rounded px-2 py-1">
            No hay razones disponibles. Crea alguna en{' '}
            <Link to="/marketing/lost-reasons" className="text-blueprint-700">
              Ajustes
            </Link>
            .
          </div>
        ) : (
          <div>
            <label className="text-[10px] font-mono uppercase text-ink-700/60">
              Razón de pérdida
            </label>
            <select
              value={reasonId}
              onChange={(e) => setReasonId(e.target.value)}
              className="w-full border border-ink-900/15 rounded px-2 py-1.5 text-sm bg-white"
            >
              {reasons.map((r) => (
                <option key={r.id} value={r.id}>
                  {r.name}
                </option>
              ))}
            </select>
          </div>
        )}
        <div className="flex justify-end gap-2 mt-4">
          <button onClick={onCancel} className="bp-btn">
            Cancelar
          </button>
          <button
            onClick={() => reasonId && onConfirm(reasonId)}
            disabled={!reasonId}
            className="bp-btn-primary"
          >
            Confirmar
          </button>
        </div>
      </div>
    </div>
  )
}
